if (!customElements.get('evolved-rail-controls')) {
  const pendingRails = new Set();
  let railFrame = 0;
  const flushRails = () => {
    railFrame = 0;
    const rails = [...pendingRails];
    pendingRails.clear();
    const measurements = rails.map(rail => [rail, rail.measure()]);
    measurements.forEach(([rail, measurement]) => rail.render(measurement));
  };
  customElements.define('evolved-rail-controls', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = document.getElementById(this.getAttribute('observes'));
      if (!this.track) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.active = !('IntersectionObserver' in window);
      if (!this.active) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.active = entries.some(entry => entry.isIntersecting);
          if (this.active) this.scheduleUpdate();
        }, {rootMargin: '300px 0px'});
        this.visibilityObserver.observe(this.track);
      }
      this.range = this.querySelector('[data-rail-range]');
      this.more = this.querySelector('[data-rail-more]');
      this.buttons = [...this.querySelectorAll('.ev-rail-controls__buttons button')];
      this.mobileMedia = matchMedia('(max-width: 699px)');
      this.mobileMedia.addEventListener('change', () => this.scheduleUpdate(), {signal});
      this.desktopMedia = matchMedia('(min-width: 1000px)');
      this.desktopMedia.addEventListener('change', () => this.scheduleUpdate(), {signal});
      this.loadedPages = new Set();
      this.buttons.forEach((button, index) => button.addEventListener('click', () => {
        if (this.isMobileCollection()) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        if (this.mobileMedia.matches && document.body.classList.contains('ev-page-product')) {
          const cards = [...this.track.querySelectorAll('product-card,.ev-guide-card')];
          const bounds = this.track.getBoundingClientRect();
          const position = Math.abs(this.track.scrollLeft);
          const points = cards.map(card => {
            const rect = card.getBoundingClientRect();
            return position + (rtl ? bounds.right - rect.right : rect.left - bounds.left);
          });
          const max = this.track.scrollWidth - this.track.clientWidth;
          const destination = index ? points.find(point => point > position + 2) ?? max : points.findLast(point => point < position - 2) ?? 0;
          this.track.scrollTo({left: (rtl ? -1 : 1) * Math.max(0, Math.min(max, destination)), behavior});
        } else {
          this.track.scrollBy({left:(index ? 1 : -1) * (rtl ? -1 : 1) * this.track.clientWidth * .9, behavior});
        }
      }, {signal}));
      this.more?.addEventListener('click', event => {
        if (this.isMobileCollection() || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.failed = false;
        this.loadMore();
      }, {signal});
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), {passive:true,signal});
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(this.track);
      this.mutationObserver = new MutationObserver(() => this.scheduleUpdate());
      this.mutationObserver.observe(this.track, {childList:true,subtree:true});
      this.scheduleUpdate();
    }
    isMobileCollection() { return document.body.classList.contains('ev-page-collection') && this.mobileMedia.matches; }
    isDesktopCollection() { return document.body.classList.contains('ev-page-collection') && this.desktopMedia.matches; }
    scheduleUpdate() {
      if (!this.active) return;
      pendingRails.add(this);
      if (!railFrame) railFrame = requestAnimationFrame(flushRails);
    }
    update() { this.scheduleUpdate(); }
    measure() {
      if (!this.isConnected || !this.active) return null;
      // Grid and mobile preview controls do not need a layout measurement.
      if (this.isMobileCollection()) return {mobile: true, hidden: !this.querySelector('[data-rail-mobile-all]')};
      if (this.isDesktopCollection()) return {grid: true, hidden: !!this.track.closest('.shopify-section--main-collection') || (!this.dataset.fetchUrl && (!this.more || this.more.hidden))};
      const width = this.track.clientWidth;
      if (!width) return null;
      const cards = [...this.track.querySelectorAll('product-card,.ev-guide-card')];
      const bounds = this.track.getBoundingClientRect();
      const shown = cards.map((card,index) => ({index,rect:card.getBoundingClientRect()})).filter(({rect}) => Math.min(rect.right,bounds.right) - Math.max(rect.left,bounds.left) >= rect.width * .5);
      const scrollWidth = this.track.scrollWidth;
      const position = Math.abs(this.track.scrollLeft);
      const overflow = scrollWidth > width + 2;
      const first = shown[0]?.index + 1, last = shown[shown.length - 1]?.index + 1;
      return {
        mobile: false,
        overflow,
        hidden: !overflow && !this.dataset.fetchUrl && (!this.more || this.more.hidden),
        previousDisabled: position < 2,
        nextDisabled: position + width >= scrollWidth - 2,
        range: shown.length ? `${first}${last === first ? '' : '–' + last} / ${cards.length}` : null,
        nearEnd: !this.failed && position + width * 1.6 >= scrollWidth
      };
    }
    render(state) {
      if (!this.isConnected || !state) return;
      if (state.grid) {
        this.track.removeAttribute('tabindex');
        this.hidden = state.hidden;
        return;
      }
      if (state.mobile) {
        this.fetchController?.abort();
        this.track.removeAttribute('tabindex');
        this.hidden = state.hidden;
        return;
      }
      if (this.track.tabIndex !== 0) this.track.tabIndex = 0;
      this.hidden = state.hidden;
      this.querySelector('.ev-rail-controls__buttons').hidden = !state.overflow;
      this.querySelector('.ev-rail-controls__hint').hidden = !state.overflow;
      if (this.buttons[0]) this.buttons[0].disabled = state.previousDisabled;
      if (this.buttons[1]) this.buttons[1].disabled = state.nextDisabled;
      if (state.range && this.range && this.range.textContent !== state.range) {
        this.range.textContent = state.range;
        this.range.setAttribute('aria-label',`${this.dataset.rangeLabel}: ${state.range}`);
      }
      if (state.nearEnd) this.loadMore();
    }
    async loadMore() {
      if (this.isMobileCollection()) return;
      const source = this.dataset.fetchUrl || (!this.more?.hidden && this.more?.getAttribute('href'));
      if (!source || this.loading || this.loadedPages.has(source)) return;
      const url = new URL(source, location.href);
      if (url.origin !== location.origin) return;
      this.loading = true;
      this.setAttribute('aria-busy','true');
      this.fetchController = new AbortController();
      const timeout = setTimeout(() => this.fetchController.abort(), 12000);
      try {
        const response = await fetch(url.href,{credentials:'same-origin',signal:this.fetchController.signal});
        if (!response.ok) throw new Error('Product page unavailable');
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        if (!this.isConnected || this.isMobileCollection()) return;
        const page = html.getElementById(this.track.id) || html.querySelector('[data-ev-rail-page]');
        if (!page) throw new Error('Product list missing');
        const list = this.track.querySelector('.product-list,.ev-guide__grid');
        const key = card => card.dataset.productId || card.getAttribute('product-id') || card.querySelector('a[href]')?.getAttribute('href')?.split('?')[0];
        const existing = new Set([...list.querySelectorAll('product-card,.ev-guide-card')].map(key));
        const fragment = document.createDocumentFragment();
        page.querySelectorAll('product-card,.ev-guide-card').forEach(card => {
          const id = key(card);
          if (!id || existing.has(id)) return;
          existing.add(id);
          fragment.append(document.importNode(card,true));
        });
        list.append(fragment);
        this.loadedPages.add(source);
        const next = page.getAttribute('data-next-url') || html.querySelector(`evolved-rail-controls[observes="${CSS.escape(this.track.id)}"] [data-rail-more]:not([hidden])`)?.getAttribute('href');
        delete this.dataset.fetchUrl;
        if (next && page.hasAttribute('data-ev-rail-page')) this.dataset.fetchUrl = next;
        if (this.more) {
          this.more.hidden = !next;
          if (next) {
            const fallback = new URL(next,location.href);
            if (fallback.searchParams.get('view') === 'evolved-rail') fallback.searchParams.set('view','evolved-filtered');
            this.more.setAttribute('href',fallback.pathname + fallback.search);
          } // Keep the native fallback URL on the hidden link.
        }
        this.querySelector('[data-rail-status]').textContent = '';
        this.scheduleUpdate();
      } catch (error) {
        if (!this.isConnected || this.isMobileCollection()) return;
        this.failed = true;
        this.querySelector('[data-rail-status]').textContent = this.dataset.loadError || '';
        if (this.more) this.more.hidden = false;
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.controller?.abort();this.controller = null;
      this.fetchController?.abort();this.resizeObserver?.disconnect();this.mutationObserver?.disconnect();
      this.visibilityObserver?.disconnect();this.active = false;
      pendingRails.delete(this);
      if (!pendingRails.size) {cancelAnimationFrame(railFrame);railFrame = 0;}
    }
  });
}
if (!customElements.get('evolved-watch-guide')) {
  customElements.define('evolved-watch-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const select = this.querySelector('[data-watch-size]');
      const update = () => this.querySelectorAll('[data-watch-panel]').forEach(panel => {panel.hidden = panel.dataset.watchPanel !== select.value;});
      select?.addEventListener('change',update,{signal:this.controller.signal});
      if (select) update();
    }
    disconnectedCallback() {this.controller?.abort();this.controller = null;}
  });
}
